import '../../../../../components/inputs/RangeDatePicker/styles.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack
} from '@chakra-ui/react';
import { isValid, parse, setDate, subDays } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { forwardRef, useEffect, useState } from 'react';
import { DateRange, Range, RangeKeyDict } from 'react-date-range';
import InputMask from 'react-input-mask';

export const DatePicker = ({
  prevLimitDate,
  onDateRangeSelected
}: {
  prevLimitDate?: Date;
  onDateRangeSelected?: (range: Range) => void;
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateState, setDateState] = useState<Range[]>([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const handleSetDate = (item: RangeKeyDict) => {
    setDateState([
      {
        startDate: item.selection.startDate,
        endDate: item.selection.endDate,
        key: 'selection'
      }
    ]);
  };

  const formattedDates = {
    startDate: new Intl.DateTimeFormat('pt-BR').format(dateState[0].startDate),
    endDate: new Intl.DateTimeFormat('pt-BR').format(dateState[0].endDate)
  };

  const handleFocusInputs = () => setShowDatePicker(true);

  const handleInitialDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget?.value;

    if (value && isValid(parse(value, 'dd/MM/yyyy', Date.now()))) {
      setDateState((state) => [
        {
          ...state[0],
          startDate: parse(value, 'dd/MM/yyyy', Date.now())
        }
      ]);
    }
  };
  const handleEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget?.value;

    if (value && isValid(parse(value, 'dd/MM/yyyy', Date.now()))) {
      setDateState((state) => [
        {
          ...state[0],
          endDate: parse(value, 'dd/MM/yyyy', Date.now())
        }
      ]);
    }
  };

  useEffect(() => {
    if (onDateRangeSelected) onDateRangeSelected(dateState[0]);
  }, [dateState]);

  useEffect(() => {
    if (onDateRangeSelected) onDateRangeSelected(dateState[0]);
  }, []);

  return (
    <VStack w={'100%'} alignItems="start" px={5}>
      <Flex gap={2} mt={7}>
        <VStack alignItems={'start'}>
          <Text as={'label'}>Data Inicial</Text>
          <InputGroup mt={'0 !important'}>
            <MaskedInput
              mask="99/99/9999"
              value={formattedDates.startDate || ''}
              onFocus={handleFocusInputs}
              onChange={handleInitialDate}
            />
            <InputRightElement>
              <CalendarIcon />
            </InputRightElement>
          </InputGroup>
        </VStack>
        <VStack alignItems={'start'}>
          <Text as={'label'}>Data Final</Text>
          <InputGroup mt={'0 !important'}>
            <MaskedInput
              mask="99/99/9999"
              value={formattedDates.endDate || ''}
              onFocus={handleFocusInputs}
              onChange={handleEndDate}
            />
            <InputRightElement>
              <CalendarIcon />
            </InputRightElement>
          </InputGroup>
        </VStack>
      </Flex>
      {showDatePicker ? (
        <Box shadow={'lg'} borderRadius={20}>
          <DateRange
            onChange={(item) => handleSetDate(item)}
            ranges={dateState}
            monthDisplayFormat="MMMM yyyy"
            showMonthAndYearPickers={false}
            weekdayDisplayFormat="EEEEE"
            months={2}
            locale={pt}
            direction="horizontal"
            maxDate={new Date()}
            minDate={prevLimitDate}
            preventSnapRefocus={true}
            calendarFocus="backwards"
          />
        </Box>
      ) : undefined}
    </VStack>
  );
};

const MaskedInput = forwardRef((props: any, ref) => (
  <InputMask inputRef={ref} mask={props.mask} {...props}>
    {(inputProps: any) => (
      <Input variant="outline" type={'text'} {...inputProps} />
    )}
  </InputMask>
));
MaskedInput.displayName = 'MaskedInput';

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.84626 2.33643C1.50665 2.33643 1.23077 2.59205 1.23077 2.90585V14.2921C1.23077 14.6059 1.50665 14.8614 1.84626 14.8614H14.1537C14.4934 14.8614 14.7692 14.6059 14.7692 14.2921V2.90585C14.7692 2.59205 14.4934 2.33643 14.1537 2.33643H1.84626ZM14.1537 16H1.84626C0.828263 16 0 15.2339 0 14.2921V2.90585C0 1.96405 0.828263 1.19778 1.84626 1.19778H14.1537C15.1717 1.19778 15.9998 1.96405 15.9998 2.90585V14.2921C15.9998 15.2339 15.1717 16 14.1537 16Z"
      fill="#001E64"
    />
    <path
      d="M15.3845 6.89075H0.615488C0.275256 6.89075 0 6.6359 0 6.32152C0 6.00695 0.275256 5.7521 0.615488 5.7521H15.3845C15.7247 5.7521 16 6.00695 16 6.32152C16 6.6359 15.7247 6.89075 15.3845 6.89075Z"
      fill="#001E64"
    />
    <path
      d="M4.25343 0.932965V2.54232C4.25343 2.70479 4.20838 2.85747 4.12971 2.99036C3.95845 3.2792 3.62631 3.47528 3.24477 3.47528C2.68782 3.47528 2.23633 3.05758 2.23633 2.54232V0.932965C2.23633 0.417702 2.68782 0 3.24477 0C3.62715 0 3.95928 0.19608 4.12971 0.485687C4.20838 0.618584 4.25343 0.771453 4.25343 0.932965Z"
      fill="#001E64"
    />
    <path
      d="M8.76905 0.932965V2.54232C8.76905 2.70479 8.72401 2.85747 8.64533 2.99036C8.47408 3.2792 8.14194 3.47528 7.7604 3.47528C7.20345 3.47528 6.75195 3.05758 6.75195 2.54232V0.932965C6.75195 0.417702 7.20345 0 7.7604 0C8.14277 0 8.47491 0.19608 8.64533 0.485687C8.72401 0.618584 8.76905 0.771453 8.76905 0.932965Z"
      fill="#001E64"
    />
    <path
      d="M13.7339 0.932965V2.54232C13.7339 2.70479 13.6889 2.85747 13.6102 2.99036C13.4389 3.2792 13.1068 3.47528 12.7252 3.47528C12.1683 3.47528 11.7168 3.05758 11.7168 2.54232V0.932965C11.7168 0.417702 12.1683 0 12.7252 0C13.1076 0 13.4397 0.19608 13.6102 0.485687C13.6889 0.618584 13.7339 0.771453 13.7339 0.932965Z"
      fill="#001E64"
    />
  </svg>
);
